import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import {
  fetchWalletCashoutsData,
  createWalletCashoutData,
  approveWalletCashoutData,
  rejectWalletCashoutData,
  completeWalletCashoutData,
  cancelWalletCashoutData,
  resetWalletCashoutState,
} from '../../store/wallet-cashout/actions';

const WalletCashout = props => {
  const dispatch = useDispatch();
  const { data, loading, error, createLoading, createError, createSuccess, actionLoading, actionError, actionSuccess } = useSelector(state => state.WalletCashout || {});
  
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedCashout, setSelectedCashout] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    bank_name: '',
    account_number: '',
    account_holder_name: '',
    iban: '',
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    dispatch(fetchWalletCashoutsData({ status: statusFilter }));
  }, [dispatch, statusFilter]);

  // Listen for success/error from Redux actions
  useEffect(() => {
    if (createError) {
      toast.error(createError);
    }
  }, [createError]);

  useEffect(() => {
    if (actionError) {
      toast.error(actionError);
    }
  }, [actionError]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleCreateCashout = (e) => {
    e.preventDefault();
    dispatch(createWalletCashoutData(formData));
  };

  // Handle create success
  useEffect(() => {
    if (createSuccess && !createLoading) {
      toast.success('Cashout request created successfully!');
      setShowCreateModal(false);
      setFormData({
        amount: '',
        payment_method: 'bank_transfer',
        bank_name: '',
        account_number: '',
        account_holder_name: '',
        iban: '',
      });
      dispatch(resetWalletCashoutState());
      dispatch(fetchWalletCashoutsData({ status: statusFilter }));
    }
  }, [createSuccess, createLoading, dispatch, statusFilter]);

  const handleApprove = () => {
    if (!selectedCashout) return;
    
    Swal.fire({
      title: 'Approve Cashout Request?',
      text: `Are you sure you want to approve this cashout request of LYD ${selectedCashout.amount}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, approve it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(approveWalletCashoutData(selectedCashout.id, notes));
      }
    });
  };

  // Handle approve success
  useEffect(() => {
    if (actionSuccess && !actionLoading && showApproveModal) {
      toast.success('Cashout request approved successfully!');
      setShowApproveModal(false);
      setSelectedCashout(null);
      setNotes('');
      dispatch(resetWalletCashoutState());
      dispatch(fetchWalletCashoutsData({ status: statusFilter }));
    }
  }, [actionSuccess, actionLoading, showApproveModal, dispatch, statusFilter]);

  const handleReject = () => {
    if (!selectedCashout || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    Swal.fire({
      title: 'Reject Cashout Request?',
      text: `Are you sure you want to reject this cashout request?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, reject it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(rejectWalletCashoutData(selectedCashout.id, rejectionReason, notes));
      }
    });
  };

  // Handle reject success
  useEffect(() => {
    if (actionSuccess && !actionLoading && showRejectModal) {
      toast.success('Cashout request rejected successfully!');
      setShowRejectModal(false);
      setSelectedCashout(null);
      setRejectionReason('');
      setNotes('');
      dispatch(resetWalletCashoutState());
      dispatch(fetchWalletCashoutsData({ status: statusFilter }));
    }
  }, [actionSuccess, actionLoading, showRejectModal, dispatch, statusFilter]);

  const handleComplete = () => {
    if (!selectedCashout) return;

    Swal.fire({
      title: 'Complete Cashout Request?',
      text: `Mark this cashout request as completed?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#17a2b8',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, complete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(completeWalletCashoutData(selectedCashout.id, notes));
      }
    });
  };

  // Handle complete success
  useEffect(() => {
    if (actionSuccess && !actionLoading && showCompleteModal) {
      toast.success('Cashout request completed successfully!');
      setShowCompleteModal(false);
      setSelectedCashout(null);
      setNotes('');
      dispatch(resetWalletCashoutState());
      dispatch(fetchWalletCashoutsData({ status: statusFilter }));
    }
  }, [actionSuccess, actionLoading, showCompleteModal, dispatch, statusFilter]);

  const handleCancel = (id) => {
    Swal.fire({
      title: 'Cancel Cashout Request?',
      text: `Are you sure you want to cancel this cashout request?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ffc107',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, cancel it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(cancelWalletCashoutData(id));
      }
    });
  };

  // Handle cancel success - only if we're not in a modal
  useEffect(() => {
    if (actionSuccess && !actionLoading && !showApproveModal && !showRejectModal && !showCompleteModal) {
      dispatch(resetWalletCashoutState());
      dispatch(fetchWalletCashoutsData({ status: statusFilter }));
    }
  }, [actionSuccess, actionLoading, showApproveModal, showRejectModal, showCompleteModal, dispatch, statusFilter]);

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    let badgeClass = "badge-secondary";
    let statusText = status;

    switch (statusLower) {
      case "pending":
        badgeClass = "badge-warning";
        statusText = "Pending";
        break;
      case "approved":
        badgeClass = "badge-info";
        statusText = "Approved";
        break;
      case "rejected":
        badgeClass = "badge-danger";
        statusText = "Rejected";
        break;
      case "processing":
        badgeClass = "badge-primary";
        statusText = "Processing";
        break;
      case "completed":
        badgeClass = "badge-success";
        statusText = "Completed";
        break;
      case "cancelled":
        badgeClass = "badge-secondary";
        statusText = "Cancelled";
        break;
      default:
        badgeClass = "badge-secondary";
    }

    return <span className={`badge ${badgeClass}`}>{statusText}</span>;
  };

  const columns = useMemo(
    () => [
      {
        header: 'Driver',
        accessorKey: 'driver_name',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Amount',
        accessorKey: 'amount',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          return `LYD ${row.original.amount}`;
        }
      },
      {
        header: 'Payment Method',
        accessorKey: 'payment_method',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const method = row.original.payment_method;
          return method ? method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A';
        }
      },
      {
        header: 'Bank Name',
        accessorKey: 'bank_name',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'IBAN',
        accessorKey: 'account_number',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          return row.original.account_number || 'N/A';
        }
      },
      {
        header: 'Account Holder Name',
        accessorKey: 'account_holder_name',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          return row.original.account_holder_name || 'N/A';
        }
      },
      {
        header: 'Status',
        accessorKey: 'status',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getStatusBadge(row.original.status),
      },
      {
        header: 'Created At',
        accessorKey: 'created_at',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          return moment(row.original.created_at).format('DD-MM-YYYY HH:mm');
        }
      },
      {
        header: 'Action',
        id: 'action',
        cell: ({ row }) => {
          const cashout = row.original;
          const status = cashout.status?.toLowerCase();

          return (
            <div className="d-flex gap-2">
              {status === 'pending' && (
                <>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => {
                      setSelectedCashout(cashout);
                      setShowApproveModal(true);
                    }}
                    disabled={actionLoading}
                  >
                    <i className="mdi mdi-check"></i> Approve
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      setSelectedCashout(cashout);
                      setShowRejectModal(true);
                    }}
                    disabled={actionLoading}
                  >
                    <i className="mdi mdi-close"></i> Reject
                  </button>
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => handleCancel(cashout.id)}
                    disabled={actionLoading}
                  >
                    <i className="mdi mdi-cancel"></i> Cancel
                  </button>
                </>
              )}
              {(status === 'approved' || status === 'processing') && (
                <button
                  className="btn btn-sm btn-info"
                  onClick={() => {
                    setSelectedCashout(cashout);
                    setShowCompleteModal(true);
                  }}
                  disabled={actionLoading}
                >
                  <i className="mdi mdi-check-circle"></i> Complete
                </button>
              )}
              {cashout.rejection_reason && (
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    Swal.fire({
                      title: 'Rejection Reason',
                      text: cashout.rejection_reason,
                      icon: 'info',
                    });
                  }}
                >
                  <i className="mdi mdi-information"></i> Reason
                </button>
              )}
            </div>
          );
        }
      }
    ],
    [actionLoading]
  );

  const transformedData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map(item => ({
      id: item.id,
      cashout_id: item.cashout_id,
      driver_name: item.driver_name || 'N/A',
      amount: item.amount,
      payment_method: item.payment_method,
      bank_name: item.bank_name || 'N/A',
      account_number: item.account_number,
      account_holder_name: item.account_holder_name,
      iban: item.iban,
      status: item.status,
      rejection_reason: item.rejection_reason,
      processed_at: item.processed_at,
      created_at: item.created_at,
    }));
  }, [data]);

  document.title = "Wallet Cashout | Moeen";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Wallet Cashout" />
        
        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-6">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            isCustomPageSize={true}
            SearchPlaceholder={`Search cashout requests... ${transformedData?.length || 0} results loaded.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create Cashout Request</h5>
                  <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
                </div>
                <form onSubmit={handleCreateCashout}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Amount (LYD)</label>
                      <input
                        type="number"
                        className="form-control"
                        step="0.01"
                        min="1"
                        required
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Payment Method</label>
                      <select
                        className="form-select"
                        required
                        value={formData.payment_method}
                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                      >
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cash">Cash</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    {formData.payment_method === 'bank_transfer' && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Bank Name</label>
                          <input
                            type="text"
                            className="form-control"
                            required
                            value={formData.bank_name}
                            onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Account Number</label>
                          <input
                            type="text"
                            className="form-control"
                            required
                            value={formData.account_number}
                            onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Account Holder Name</label>
                          <input
                            type="text"
                            className="form-control"
                            required
                            value={formData.account_holder_name}
                            onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">IBAN</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.iban}
                            onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Create Request</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Approve Modal */}
        {showApproveModal && selectedCashout && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Approve Cashout Request</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowApproveModal(false);
                    setSelectedCashout(null);
                    setNotes('');
                  }}></button>
                </div>
                <div className="modal-body">
                  <p><strong>Amount:</strong> LYD {selectedCashout.amount}</p>
                  <p><strong>Driver:</strong> {selectedCashout.driver_name}</p>
                  <div className="mb-3">
                    <label className="form-label">Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowApproveModal(false);
                    setSelectedCashout(null);
                    setNotes('');
                  }}>Cancel</button>
                  <button type="button" className="btn btn-success" onClick={handleApprove} disabled={actionLoading}>
                    Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && selectedCashout && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reject Cashout Request</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowRejectModal(false);
                    setSelectedCashout(null);
                    setRejectionReason('');
                    setNotes('');
                  }}></button>
                </div>
                <div className="modal-body">
                  <p><strong>Amount:</strong> LYD {selectedCashout.amount}</p>
                  <p><strong>Driver:</strong> {selectedCashout.driver_name}</p>
                  <div className="mb-3">
                    <label className="form-label">Rejection Reason *</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      required
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Enter rejection reason..."
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowRejectModal(false);
                    setSelectedCashout(null);
                    setRejectionReason('');
                    setNotes('');
                  }}>Cancel</button>
                  <button type="button" className="btn btn-danger" onClick={handleReject} disabled={actionLoading || !rejectionReason.trim()}>
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Complete Modal */}
        {showCompleteModal && selectedCashout && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Complete Cashout Request</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowCompleteModal(false);
                    setSelectedCashout(null);
                    setNotes('');
                  }}></button>
                </div>
                <div className="modal-body">
                  <p><strong>Amount:</strong> LYD {selectedCashout.amount}</p>
                  <p><strong>Driver:</strong> {selectedCashout.driver_name}</p>
                  <div className="mb-3">
                    <label className="form-label">Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowCompleteModal(false);
                    setSelectedCashout(null);
                    setNotes('');
                  }}>Cancel</button>
                  <button type="button" className="btn btn-info" onClick={handleComplete} disabled={actionLoading}>
                    Complete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

WalletCashout.propTypes = {
  t: PropTypes.any
};

export default WalletCashout;

